import { useEffect, useId } from 'react';
import { create } from 'zustand';

import { brand } from '../config/brand';

/**
 * What the browser tab says.
 *
 * ## Why a store rather than each page writing `document.title`
 *
 * The obvious version - an effect per page that assigns the title, and one in
 * the shell that resets it on navigation - has an ordering bug that only shows
 * up sometimes. React runs child effects before parent ones, so the shell's
 * reset fires *after* the page has set its own title and wipes it. The bug is
 * invisible on a fast machine and appears on a slow one.
 *
 * Here a component declares what it wants and one effect writes it. Leaving
 * drops the claim, so a title can never outlive the thing that set it - which
 * is the failure worth designing out. A tab reading "One More Sign" while
 * somebody is on their settings page is the kind of wrongness nobody reports
 * and everybody notices.
 *
 * ## Why the claims are a stack and not one slot
 *
 * Because two things can be on screen at once. A drawer opens over the profile
 * underneath it, and both are mounted; with a single slot the drawer's title
 * would win - correctly - and then closing it would clear the tab to the bare
 * brand name, because the page below has no reason to re-run its effect. The
 * stack pops back to whatever was underneath, which is what the person looking
 * at the screen would expect.
 *
 * ## The suffix is the brand, and it comes last
 *
 * "Discover — GetVibed", not the other way round. A tab strip truncates from
 * the right, and with eight tabs open the first dozen characters are all
 * anybody gets - so they have to be the part that differs.
 */
interface Claim {
  id: string;
  title: string;
}

interface TitleState {
  claims: Claim[];
  claim: (id: string, title: string | null) => void;
  release: (id: string) => void;
}

const useTitleStore = create<TitleState>((set) => ({
  claims: [],
  claim: (id, title) =>
    set((state) => {
      const rest = state.claims.filter((entry) => entry.id !== id);

      // Dropped and re-pushed rather than updated in place: a component that
      // starts with no title and gets one when its data lands should end up on
      // top, the same as one that mounted knowing its title.
      return { claims: title === null ? rest : [...rest, { id, title }] };
    }),
  release: (id) => set((state) => ({ claims: state.claims.filter((entry) => entry.id !== id) })),
}));

/**
 * Claim the document title for as long as this component is mounted.
 *
 * `null` or omitted means "nothing specific" - the tab falls back to whatever
 * is underneath, or to the brand. That is the right thing for a page still
 * loading: naming it "Loading" would put that word in the browser history.
 *
 * Pass the raw name of the thing. The brand suffix is added here so it cannot
 * be appended twice, or forgotten on one page out of thirty.
 */
export function useDocumentTitle(title?: string | null): void {
  const id = useId();
  const claim = useTitleStore((state) => state.claim);
  const release = useTitleStore((state) => state.release);

  useEffect(() => {
    const trimmed = title?.trim();

    claim(id, trimmed ? trimmed : null);

    return () => release(id);
  }, [id, title, claim, release]);
}

/**
 * Writes the title. Mounted once, in the shell.
 *
 * A component rather than a hook called from `AppShell` because it subscribes
 * to the store, and a subscription in the shell would re-render the whole
 * application - including the dock, which must not remount - every time a
 * title changed.
 */
export function DocumentTitle(): null {
  const title = useTitleStore((state) => state.claims.at(-1)?.title ?? null);

  useEffect(() => {
    document.title = title === null ? brand.name : `${title} — ${brand.name}`;
  }, [title]);

  return null;
}
